import { BaseColorType, DATE_TIME_FORMAT, dateFormat } from '@evrekadev/evreka-ui-components'
import { sendNotification } from 'components'
import { HistoryRequest, HistoryResponse } from 'containers/HistoryV2/types'
import { reportsApi } from 'services/ReportManagement/reports'
import { ActionResponse, GenericResponse } from 'services/types'
import { sendExportNotification } from 'utils'
import { opsGeneralApi } from './opsGeneral'
import { routeApi } from './routes'
import {
  ActionNoteRequest,
  DeleteTaskRequest,
  DeleteTaskResponse,
  GenericExportListRequest,
  GenericSearchResponse,
  GenericSuccessResponse,
  MutationResponse,
  RoutesByFilterResponse,
  SearchTaskRequest,
  SearchTaskResponse,
  SearchTasksByParamsRequest,
  TaskAddRequest,
  TaskAddResponse,
  TaskCardItem,
  TaskChangeStatusRequest,
  TaskDetailForParcelRequest,
  TaskDetailForParcelResponse,
  TaskDetailRequest,
  TaskDetailResponse,
  TaskEditDetailFieldsRequest,
  TaskEditDetailFieldsResponse,
  TaskMapPointsRequest,
  TaskMapPointsResponse,
  TaskMapPopupDetailsRequest,
  TaskMapPopupDetailsResponse,
  TaskOptimizeFilterRequest,
  TaskOptimizeServiceResponse,
  TasksCountByFilterResponse,
  TasksRequest,
  TasksResponse,
  TaskTransitionDetailPageRequest,
  TaskTransitionDetailPageResponse,
  TaskTransitionDetailsRequest,
  TaskTransitionDetailsResponse,
  ValidateTaskUsabilityRequest,
  ValidateTaskUsabilityResponse,
  type CockpitTaskListRawResponse,
} from './types'
import { DropdownOptionTemplate } from 'types/common'

export const taskApi = routeApi.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<TasksResponse, TasksRequest>({
      query: ({ clientId, order_id, ...data }) => ({
        url: `clients/${clientId}/ops_management/list_task/`,
        method: 'POST',
        data,
        params: {
          route_id: data.route_id,
          service_point_id: data.service_point_id,
          order_id: order_id,
          inbound_id: data.inbound_id,
          task_plan: data.task_plan_id,
          consignment_id: data.consignment_id,
        },
      }),
      providesTags: ['Tasks'],
    }),
    getTaskDetails: builder.query<TaskDetailResponse, TaskDetailRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/task_detail/`,
        method: 'POST',
        data,
      }),
      providesTags: ['TaskDetail'],
    }),
    getTaskDetailsForParcel: builder.query<TaskDetailForParcelResponse, TaskDetailForParcelRequest>(
      {
        query: ({ clientId, ...data }) => ({
          url: `clients/${clientId}/ops_management/tasks/get_task_detail/`,
          method: 'POST',
          data,
        }),
        providesTags: ['TaskDetail'],
      },
    ),
    addAdHocTask: builder.mutation<TaskAddResponse, TaskAddRequest>({
      query({ clientId, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/create_task/`,
          method: 'POST',
          data,
        }
      },
      invalidatesTags: ['Tasks', 'TaskHistory', 'TasksInMaps'],
    }),
    getTaskTransitions: builder.query<
      TaskTransitionDetailPageResponse,
      TaskTransitionDetailPageRequest
    >({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/task_transitions/`,
        method: 'POST',
        data,
      }),
      providesTags: ['TaskTransitionDetailPage'],
    }),
    getTaskPointsForMap: builder.query<TaskMapPointsResponse, TaskMapPointsRequest>({
      query: ({ clientId, route_id }) => ({
        url: `clients/${clientId}/ops_management/get_tasks_for_map/`,
        method: 'GET',
        params: {
          route_id,
        },
      }),
      providesTags: ['TasksInMaps'],
    }),
    getEditTaskFields: builder.query<TaskEditDetailFieldsResponse, TaskEditDetailFieldsRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/get_edit_task_fields/`,
        method: 'GET',
        params: {
          task: data.task,
        },
      }),
      providesTags: ['EditTasksFields'],
    }),
    editTaskAssignment: builder.mutation<GenericSuccessResponse, any>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/edit_task_assignment/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'Tasks',
        'TaskDetail',
        'EditTasksFields',
        'TaskItineraryList',
        'TaskHistory',
        'TaskTransitionDetails',
      ],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(routeApi.util.invalidateTags(['RouteHistory', 'Routes', 'RouteDetail']))
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportTasks: builder.mutation<GenericSuccessResponse, GenericExportListRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/export/task/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {}
      },
    }),
    changeTaskStatus: builder.mutation<GenericResponse, TaskChangeStatusRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/ops_management/change_task_status/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Tasks', 'TaskHistory', 'TaskDetail'],
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getTasksCountByFilter: builder.query<TasksCountByFilterResponse, TaskOptimizeFilterRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/tasks/get_tasks_count/`,
        method: 'POST',
        data,
      }),
    }),
    getRoutesByFilter: builder.query<RoutesByFilterResponse, TaskOptimizeFilterRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/route/get_routes/`,
        method: 'POST',
        data,
      }),
    }),
    optimizeTaskService: builder.mutation<TaskOptimizeServiceResponse, TaskOptimizeFilterRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/tasks/optimize/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['TaskItineraryList'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    optimizeRoute: builder.mutation<TaskOptimizeServiceResponse, TaskOptimizeFilterRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/route/${data.id}/optimize_route/`,
        method: 'GET',
      }),
      invalidatesTags: ['TaskItineraryList'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchTask: builder.mutation<SearchTaskResponse, SearchTaskRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/search/task/`,
        method: 'POST',
        data,
      }),
    }),
    validateTaskUsability: builder.mutation<
      ValidateTaskUsabilityResponse,
      ValidateTaskUsabilityRequest
    >({
      query: ({ external_client_id, ...data }) => ({
        url: `clients/${external_client_id}/validate/task/`,
        method: 'POST',
        data,
      }),
    }),

    upsertNoteForTask: builder.mutation<ActionResponse, ActionNoteRequest>({
      query({ clientId, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/task/upsert_note/`,
          method: 'POST',
          data,
        }
      },
      invalidatesTags: ['Tasks', 'TaskHistory', 'TaskDetail', 'EditTasksFields'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteTask: builder.mutation<DeleteTaskResponse, DeleteTaskRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/task/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: [
        'Tasks',
        'EditTasksFields',
        'TaskItineraryList',
        'TaskHistory',
        'TasksInMaps',
        'TaskDetail',
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),

    getTaskMapPopupDetails: builder.query<TaskMapPopupDetailsResponse, TaskMapPopupDetailsRequest>({
      query: ({ clientId, taskId }) => ({
        url: `clients/${clientId}/ops_management/task/${taskId}/map/detail/`,
        method: 'GET',
      }),
      providesTags: ['TaskDetail', 'EditTasksFields'],
    }),
    searchTasksByParams: builder.query<Array<TaskCardItem>, SearchTasksByParamsRequest>({
      queryFn: async (arg, api) => {
        try {
          const res = await api.dispatch(
            opsGeneralApi.endpoints.genericSearchByParams.initiate({
              clientId: arg.clientId,
              model_name: 'task',
              requested_values: [
                'id',
                'status_category__id',
                'status_category__name',
                'status_category__color',
                'status_category__state',
                'due_date',
                'create_time',
                'last_updated',
                'service_point__id',
                'service_point__name',
                'service_point__type',
                'info',
                'address',
                'latitude',
                'longitude',
                'task_template__name',
                'task_order_tasks__id',
                'task_order_tasks__name',
                'operation_date',
                'operation__id',
                'operation__name',
                'shift__start_time',
                'shift__end_time',
                'client__client_tz',
                'route_plan__name',
                'route_plan__id',
                'route__route_id',
                'route__route_name',
                // 'route__routedetailrecord__sequence',
              ],
              search_fields: [
                'operation__id',
                'route__date',
                ...(arg.status?.length ? ['status_category__id'] : []),
              ],
              search_queries: [
                arg.operations ?? arg.operation,
                arg.date,
                ...(arg.status?.length ? [arg.status] : []),
              ],
              filter_expressions: [
                arg.operations ? 'in' : 'exact',
                'exact',
                ...(arg.status?.length ? ['in'] : []),
              ],
              order_by: 'id',
              requested_labels: ['id'],
            }),
          )

          const backendColorHex: {
            [key: string]: BaseColorType
          } = {
            '#62BA5D': 'primary' as BaseColorType, // BE primary
            '#ADB4CB': 'grey',
            '#FBFCFD': 'white',
            '#E3524F': 'red',
            '#3BA935': 'green',
            '#FFD600': 'yellow',
            '#12A4ED': 'blue',
            '#FEA800': 'orange',
          }

          const mappedData = (res as any).data.map((data: GenericSearchResponse[0]) => {
            const color =
              backendColorHex[data.data.status_category__color] === ('primary' as BaseColorType)
                ? 'green'
                : backendColorHex[data.data.status_category__color]
            return {
              id: data.data.id,
              create_time: dateFormat(data.data.create_time, DATE_TIME_FORMAT, arg.timezone),
              last_updated: dateFormat(data.data.last_updated, DATE_TIME_FORMAT, arg.timezone),
              shift: data.data.shift__start_time
                ? data.data.shift__start_time.substring(0, data.data.shift__start_time.length - 3) +
                  '-' +
                  data.data?.shift__end_time?.substring(0, data.data?.shift__end_time.length - 3)
                : '-',
              operation_date: data.data.operation_date,
              status: {
                id: data.data.status_category__id,
                name: data.data.status_category__name,
                color: color,
                hexColor: data.data.status_category__color,
                state: data.data.status_category__state,
              },
              task_template: data.data?.task_template__name,
              due_date: data.data.due_date,
              //
              address: data.data?.address,
              latitude: data.data.latitude,
              longitude: data.data.longitude,
              servicePoint: data.data.service_point__id
                ? {
                    id: data.data.service_point__id,
                    name: data.data.service_point__name,
                    type: data.data.service_point__type,
                  }
                : null,
              route: data.data.route__route_id
                ? { id: data.data.route__route_id, name: data.data.route__route_name }
                : null,
              routePlan: data.data.route_plan__id
                ? { id: data.data.route_plan__id, name: data.data.route_plan__name }
                : null,
              operation_name: data.data.operation__name,
              // taskPlan: data.task_plan,
              // sequence: data.sequence,
            }
          })

          return { data: mappedData }
        } catch (err) {
          return { data: [] }
        }
      },
    }),
    getCockpitTasks: builder.query<
      Array<TaskCardItem>,
      { clientId: number; operation_id_list: Array<number>; date: string; timezone: string }
    >({
      query: ({ clientId, ...body }) => ({
        url: `clients/${clientId}/ops_management/cockpit/tasks/`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (res: CockpitTaskListRawResponse, _, { timezone }) => {
        const backendColorHex: {
          [key: string]: BaseColorType
        } = {
          '#62BA5D': 'primary' as BaseColorType, // BE primary
          '#ADB4CB': 'grey',
          '#FBFCFD': 'white',
          '#E3524F': 'red',
          '#3BA935': 'green',
          '#FFD600': 'yellow',
          '#12A4ED': 'blue',
          '#FEA800': 'orange',
        }
        return res.map((t) => ({
          id: t.id,
          create_time: dateFormat(t.create_time, DATE_TIME_FORMAT, timezone),
          last_updated: dateFormat(t.last_updated, DATE_TIME_FORMAT, timezone),
          shift: t.shift__start_time
            ? t.shift__start_time.substring(0, t.shift__start_time.length - 3) +
              '-' +
              t?.shift__end_time?.substring(0, t?.shift__end_time.length - 3)
            : '-',
          operation_date: convertDateFormat(t.operation_date), // from YYYY-MM-DD to DD.MM.YYYY
          due_date: convertDateFormat(t.due_date), // from YYYY-MM-DD to DD.MM.YYYY
          status: {
            id: t.status_category__id,
            name: t.status_category__name,
            color:
              backendColorHex[t.status_category__color] === ('primary' as BaseColorType)
                ? 'green'
                : backendColorHex[t.status_category__color],
            hexColor: t.status_category__color,
            state: t.status_category__state,
          },
          task_template: t.task_template__name,
          address: t.address,
          latitude: t.latitude,
          longitude: t.longitude,
          servicePoint: t.service_point__id
            ? {
                id: t.service_point__id,
                name: t.service_point__name!,
                type: t.service_point__type!,
              }
            : null,
          route: t.route__route_id ? { id: t.route__route_id, name: t.route__route_name } : null,
          routePlan: t.route_plan__id ? { id: t.route_plan__id, name: t.route_plan__name! } : null,
          operation_name: t.operation__name,
          sequence: t.route__routedetailrecord__sequence,
          note: t.info,
          taskPlan: null,
        }))
      },
    }),
    getTaskTransitionDetails: builder.query<
      TaskTransitionDetailsResponse,
      TaskTransitionDetailsRequest
    >({
      query: ({ clientId, taskStatus, task_id }) => ({
        url: `clients/${clientId}/ops_management/tasks/${task_id}/task_transition_details/?task_status_category=${taskStatus}`,
        method: 'GET',
        errorOverride: true,
      }),
      providesTags: ['TaskTransitionDetails'],
    }),
    taskTransition: builder.mutation<MutationResponse, { clientId: number; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/ops_management/task_transition/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'Tasks',
        'TaskDetail',
        'EditTasksFields',
        'TaskItineraryList',
        'TaskTransitionDetails',
        'TaskTransitionDetailPage',
        'TaskHistory',
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    taskTransitionEdit: builder.mutation<MutationResponse, { clientId: number; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/ops_management/task_transition_edit/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'Tasks',
        'EditTasksFields',
        'TaskItineraryList',
        'TaskTransitionDetails',
        'TaskTransitionDetailPage',
        'TaskHistory',
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    taskHistory: builder.query<
      HistoryResponse & MutationResponse,
      HistoryRequest & { clientId: number; taskId: number }
    >({
      query({ clientId, taskId, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/task/${taskId}/history/`,
          method: 'POST',
          data,
        }
      },
      providesTags: ['TaskHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getTaskStatuses: builder.query<
      {
        data: Array<{
          name: string
          state: string
          color: Exclude<BaseColorType, 'transparent' | 'white' | 'black'>
        }>
      },
      { client_id: number; operation_id: number }
    >({
      query: ({ client_id, operation_id }) => ({
        url: `clients/${client_id}/ops_management/task_status_categories/list/?operation_id=${operation_id}`,
        method: 'GET',
      }),
    }),
    addAttachmentsToTasks: builder.mutation<
      MutationResponse & {
        failure_list: Array<{ id: number; message: string }>
        success_list: Array<{ id: number; message: string }>
      },
      { clientId: number; body: FormData }
    >({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/ops_management/task/upload_attachments/`,
        method: 'POST',
        data: body,
        errorOverride: true,
      }),
      invalidatesTags: ['TaskDetail', 'TaskHistory'],
    }),
    getTaskItems: builder.query<
      Array<DropdownOptionTemplate<number>[0] & { operation_id: number }>,
      { clientId: number; operationIds?: number[] }
    >({
      query: ({ clientId, operationIds }) => ({
        url: `clients/${clientId}/ops_management/get_task_items/`,
        method: 'GET',
        params: {
          operation_id: operationIds?.join(','),
        },
      }),
    }),
  }),

  overrideExisting: false,
})

export const {
  useGetTasksQuery,
  useGetTaskTransitionsQuery,
  useGetTaskDetailsQuery,
  useGetTaskDetailsForParcelQuery,
  useGetTaskPointsForMapQuery,
  useExportTasksMutation,
  useAddAdHocTaskMutation,
  useEditTaskAssignmentMutation,
  useGetEditTaskFieldsQuery,
  useChangeTaskStatusMutation,
  useOptimizeTaskServiceMutation,
  useGetTasksCountByFilterQuery,
  useGetRoutesByFilterQuery,
  useSearchTaskMutation,
  useValidateTaskUsabilityMutation,
  useUpsertNoteForTaskMutation,
  useDeleteTaskMutation,
  useGetTaskMapPopupDetailsQuery,
  useOptimizeRouteMutation,
  useSearchTasksByParamsQuery,
  useLazySearchTasksByParamsQuery,
  useGetTaskTransitionDetailsQuery,
  useTaskTransitionMutation,
  useTaskTransitionEditMutation,
  useTaskHistoryQuery,
  useGetTaskStatusesQuery,
  useAddAttachmentsToTasksMutation,
  useLazyGetCockpitTasksQuery,
  useGetTaskItemsQuery,
} = taskApi

const convertDateFormat = (date: string): string => {
  const [year, month, day] = date.split('-')
  return `${day}.${month}.${year}`
}
